import * as Phaser from "phaser";
import {
  BasicOverlay,
  BasicOverlayData,
} from "$/game/prefabs/scenes/overlays/BasicOverlay.ts";
import { RoundedTextButton } from "$/game/prefabs/ui/RoundedTextButton.ts";

export type ContinueGameOverlayData = BasicOverlayData & {
  score: number;
  onContinue: () => void;
  onResults: () => void;
};

export class ContinueGameOverlay extends BasicOverlay {
  private timerText!: Phaser.GameObjects.Text;
  private timerCircle!: Phaser.GameObjects.Graphics;
  private countdown: number = 5;
  private timerEvent!: Phaser.Time.TimerEvent;
  private circleRadius: number = 60;
  private centerX: number = 0;
  private centerY: number = 0;

  constructor() {
    super({ key: "ContinueGameOverlay" });
  }

  override create(data: ContinueGameOverlayData) {
    // Call super.create with showCloseButton set to false
    super.create({ ...data, showCloseButton: false });

    // Reset countdown for fresh timer
    this.countdown = 5;

    const { width, height } = this.cameras.main;

    // Title text
    const titleText = this.add.text(
      width / 2,
      200,
      this.keys.strings.catchFoodScene.continueTitle,
      {
        fontSize: "48px",
        color: "#E97946",
        fontFamily: "Phantom-Muff",
        fontStyle: "bold",
      },
    );
    titleText.setOrigin(0.5, 0);

    // Current score text
    const scoreText = this.add.text(
      width / 2,
      280,
      `${this.keys.strings.catchFoodScene.score}: ${data.score}`,
      {
        fontSize: "40px",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
        fontFamily: "Phantom-Muff",
      },
    );
    scoreText.setOrigin(0.5, 0);

    // Timer circle position
    this.centerX = width / 2;
    this.centerY = 400;

    // Timer circle graphics
    this.timerCircle = this.add.graphics();
    this.drawTimerCircle();

    // Timer text (centered on circle)
    this.timerText = this.add.text(
      this.centerX,
      this.centerY,
      `${this.countdown}`,
      {
        fontSize: "48px",
        color: "#735933",
        fontFamily: "Phantom-Muff",
        fontStyle: "bold",
      },
    );
    this.timerText.setOrigin(0.5, 0.5);

    // Continue button
    const continueButton = new RoundedTextButton(
      this,
      width / 2,
      550,
      this.keys.strings.catchFoodScene.continueButton,
      323,
      72,
      20,
      this.global.TUNER_TWA_CONFIG.palette.success,
      this.global.TUNER_TWA_CONFIG.palette.success + 0x100000,
      {
        fontSize: "40px",
        fontStyle: "normal",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    );

    continueButton.on("pointerdown", async () => {
      if (this.settings.getSetting("isVibrationEnabled")) {
        try {
          this.telegram.twaSDK.hapticFeedback.impactOccurred("medium");
        } catch {}
      }
      // Stop the timer
      if (this.timerEvent) {
        this.timerEvent.destroy();
      }

      // Show ad for continuing game
      try {
        const adResult = await this.telegram.showAd("continueGame");

        if (adResult) {
          // Ad was watched successfully, continue the game
          data.onContinue();
        } else {
          // Ad wasn't watched to completion or error occurred, go to results
          data.onResults();
        }
      } catch (error) {
        console.error("Error showing continue game ad:", error);
        // In case of error, go to results
        data.onResults();
      }
    });
    this.add.existing(continueButton);

    // Results button
    const resultsButton = new RoundedTextButton(
      this,
      width / 2,
      700,
      this.keys.strings.catchFoodScene.resultsButton,
      323,
      72,
      20,
      this.global.TUNER_TWA_CONFIG.palette.primary,
      this.global.TUNER_TWA_CONFIG.palette.primary + 0x100000,
      {
        fontSize: "40px",
        fontStyle: "normal",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    );

    resultsButton.on("pointerdown", () => {
      if (this.settings.getSetting("isVibrationEnabled")) {
        this.telegram.twaSDK.hapticFeedback.impactOccurred("medium");
      }
      // Stop the timer
      if (this.timerEvent) {
        this.timerEvent.destroy();
      }
      data.onResults();
    });
    this.add.existing(resultsButton);

    // Start the countdown timer
    this.startCountdown(data);
  }

  private drawTimerCircle(): void {
    this.timerCircle.clear();

    const progress = this.countdown / 5; // 5 is max countdown
    const angle = Phaser.Math.DegToRad(360 * progress);

    // Draw background circle (gray)
    this.timerCircle.lineStyle(8, 0xcccccc, 1);
    this.timerCircle.beginPath();
    this.timerCircle.arc(
      this.centerX,
      this.centerY,
      this.circleRadius,
      0,
      Phaser.Math.DegToRad(360),
    );
    this.timerCircle.strokePath();

    // Draw progress circle (colored)
    const color = this.countdown > 2
      ? 0x4CAF50
      : this.countdown > 1
      ? 0xFFC107
      : 0xF44336; // Green -> Yellow -> Red
    this.timerCircle.lineStyle(8, color, 1);
    this.timerCircle.beginPath();
    this.timerCircle.arc(
      this.centerX,
      this.centerY,
      this.circleRadius,
      Phaser.Math.DegToRad(-90),
      Phaser.Math.DegToRad(-90) + angle,
      false,
    );
    this.timerCircle.strokePath();
  }

  private startCountdown(data: ContinueGameOverlayData): void {
    this.timerEvent = this.time.addEvent({
      delay: 1000,
      callback: () => {
        this.countdown--;
        this.timerText.setText(`${this.countdown}`);
        this.drawTimerCircle();

        if (this.countdown <= 0) {
          // Timer expired, go to results
          this.timerEvent.destroy();
          data.onResults();
        }
      },
      callbackScope: this,
      loop: true,
    });
  }

  override close() {
    // Stop the timer when closing the overlay
    if (this.timerEvent) {
      this.timerEvent.destroy();
    }
    super.close();
  }
}
